// Müşteri ekleme/düzenleme modal'ı
import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient, useQuery } from 'react-query';
import { FiX } from 'react-icons/fi';
import { customerService } from '../../services/customerService';
import { userService } from '../../services/userService';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';

const CustomerModal = ({ customer, onClose, onSuccess }) => {
  const queryClient = useQueryClient();
  const { user: currentUser } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue
  } = useForm();

  // Kullanıcıları çek
  const { data: usersData, isLoading: usersLoading } = useQuery(
    'users',
    () => userService.getUsers(),
    {
      select: (data) => data.users || []
    }
  );

  // Müşteri oluşturma/güncelleme mutation'ı
  const saveCustomerMutation = useMutation(
    (data) => {
      if (customer) {
        return customerService.updateCustomer(customer.id, data);
      } else {
        return customerService.createCustomer(data);
      }
    },
    {
      onSuccess: (data) => {
        toast.success(customer ? 'Müşteri güncellendi' : 'Müşteri oluşturuldu');
        queryClient.invalidateQueries('customers');
        onSuccess();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'İşlem başarısız');
      }
    }
  );

  // Form'u müşteri verileriyle doldur
  useEffect(() => {
    if (customer && usersData) {
      // Düzenleme modu - mevcut müşteri verilerini doldur
      setValue('customer_name', customer.customer_name);
      setValue('customer_address', customer.customer_address);
      setValue('customer_phone', customer.customer_phone);
      setValue('customer_account_manager', customer.customer_account_manager);
      setValue('cutomer_status', customer.cutomer_status);
    } else if (!customer && usersData && currentUser) {
      // Yeni müşteri oluşturma modu - giriş yapan kullanıcıyı otomatik seç
      setValue('customer_account_manager', currentUser.id);
    }
  }, [customer, usersData, currentUser, setValue]);

  // Form gönderimi
  const onSubmit = async (data) => {
    try {
      setIsLoading(true);
      await saveCustomerMutation.mutateAsync(data);
    } catch (error) {
      console.error('Form gönderim hatası:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content max-w-2xl">
        {/* Modal header */}
        <div className="modal-header">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-white">
              {customer ? 'Müşteri Düzenle' : 'Yeni Müşteri'}
            </h3>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-300"
            >
              <FiX size={20} />
            </button>
          </div>
        </div>

        {/* Modal body */}
        <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-6">
          {/* Müşteri adı */}
          <div>
            <label htmlFor="customer_name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Müşteri Adı *
            </label>
            <input
              id="customer_name"
              type="text"
              {...register('customer_name', { required: 'Müşteri adı gerekli' })}
              className={`input-field ${errors.customer_name ? 'input-error' : ''}`}
              placeholder="Müşteri adını girin"
            />
            {errors.customer_name && (
              <p className="mt-1 text-sm text-red-600">{errors.customer_name.message}</p>
            )}
          </div>

          {/* Adres */}
          <div>
            <label htmlFor="customer_address" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Adres
            </label>
            <textarea
              id="customer_address"
              rows={3}
              {...register('customer_address')}
              className={`input-field ${errors.customer_address ? 'input-error' : ''}`}
              placeholder="Müşteri adresini girin"
            />
            {errors.customer_address && (
              <p className="mt-1 text-sm text-red-600">{errors.customer_address.message}</p>
            )}
          </div>

          {/* Telefon */}
          <div>
            <label htmlFor="customer_phone" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Telefon Numarası
            </label>
            <input
              id="customer_phone"
              type="tel"
              {...register('customer_phone')}
              className={`input-field ${errors.customer_phone ? 'input-error' : ''}`}
              placeholder="Telefon numarasını girin"
            />
            {errors.customer_phone && (
              <p className="mt-1 text-sm text-red-600">{errors.customer_phone.message}</p>
            )}
          </div>

          {/* Hesap yöneticisi */}
          <div>
            <label htmlFor="customer_account_manager" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Müşteri Temsilcisi *
            </label>
            <select
              id="customer_account_manager"
              {...register('customer_account_manager', { required: 'Hesap yöneticisi gerekli' })}
              className={`input-field ${errors.customer_account_manager ? 'input-error' : ''}`}
              disabled={usersLoading}
            >
              <option value="">Müşteri Temsilcisi Seçin</option>
              {usersData
                ?.filter((user) => {
                  // User tipindeki kullanıcılar sadece kendilerini görebilir
                  if (currentUser?.user_type === 'user') {
                    return user.id === currentUser.id;
                  }
                  // Admin ve Manager tüm kullanıcıları görebilir
                  return true;
                })
                ?.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.user_fullname}
                  </option>
                ))}
            </select>
            {errors.customer_account_manager && (
              <p className="mt-1 text-sm text-red-600">{errors.customer_account_manager.message}</p>
            )}
            {usersLoading && (
              <p className="mt-1 text-sm text-gray-500">Kullanıcılar yükleniyor...</p>
            )}
          </div>

          {/* Durum */}
          <div>
            <label htmlFor="cutomer_status" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Durum
            </label>
            <select
              id="cutomer_status"
              {...register('cutomer_status')}
              className="input-field"
            >
              <option value={1}>Aktif</option>
              <option value={0}>Pasif</option>
            </select>
          </div>

          {/* Modal footer */}
          <div className="flex items-center justify-end space-x-3 pt-6">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary"
              disabled={isLoading}
            >
              İptal
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={isLoading}
            >
              {isLoading ? 'Kaydediliyor...' : (customer ? 'Güncelle' : 'Oluştur')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CustomerModal;
